import { isEmpty, isNil } from 'lodash'

import { driveRootLabel } from '@/views/Organizations/components/GoogleDriveConnectionForm.helpers'
import {
  PENDING_STATUS,
  browseRowsOf,
  effectiveBlockState,
  rootKey,
} from '@/views/Organizations/components/ManageFilesModal.helpers'

export const REVIEW_STATE = { SCANNING: 'scanning', AWAITING: 'awaiting_review' }

export const REVIEW_STATUS = {
  PAUSED: {
    label: 'Paused',
    variant: 'critical',
    tooltip: 'Syncing is paused for this connection. Use Resume syncing below to start it again.',
  },
  SCANNING: { label: 'Scanning folders', variant: 'informative', tooltip: null },
  SCAN_FAILED: { label: "Couldn't scan", variant: 'critical', tooltip: null },
  READY: { label: 'Ready for review', variant: 'warning', tooltip: null },
}

const NOT_SYNCED_YET_STATUS = { label: 'Not synced yet', variant: null, tooltip: null }

// Paused wins: the scan sweep skips an inactive connection. Sync rows still loading (undefined)
// or failed (null) are not a failed scan.
export const reviewStatusOf = (connection, syncState) => {
  if (isNil(connection.review_state)) {
    return null
  }
  if (!connection.active) {
    return REVIEW_STATUS.PAUSED
  }
  if (connection.review_state === REVIEW_STATE.AWAITING) {
    return REVIEW_STATUS.READY
  }
  const scanFailed = !isNil(syncState) && syncState.rows.some((row) => !isNil(row.last_error))

  return scanFailed ? REVIEW_STATUS.SCAN_FAILED : REVIEW_STATUS.SCANNING
}

export const reviewRootsOf = (apiRoots) =>
  apiRoots.map((root) => ({
    driveId: root.drive_id,
    rootId: root.root_id,
    label: root.label ?? driveRootLabel(root.drive_id),
    lastSyncedAt: null,
  }))

// Recursive rather than a loop, so a failed page ends the listing.
export const fetchEveryPage = async (fetchPage, cursor = null, items = []) => {
  const { data, error } = await fetchPage(cursor)
  if (!isNil(error)) {
    return { error }
  }
  const listed = [...items, ...data.items]

  return isNil(data.next_cursor)
    ? { data: { ...data, items: listed } }
    : fetchEveryPage(fetchPage, data.next_cursor, listed)
}

const isSameItem = (left, right) =>
  left.drive_id === right.drive_id && left.item_id === right.item_id

// A scope root nested under another has no block row of its own, so it can't be unticked.
export const isNestedRoot = (nestedRoots, item) =>
  nestedRoots.some((nested) => isSameItem(nested, item))

export const topLevelItemsOf = (cache, root, nestedRoots = []) => {
  const listing = cache.listings[rootKey(root)]
  const keys = isNil(listing) ? [] : listing.keys

  return keys.map((key) => cache.items[key]).filter((item) => !isNestedRoot(nestedRoots, item))
}

export const isListingComplete = (cache, root) => {
  const listing = cache.listings[rootKey(root)]

  return !isNil(listing) && !listing.loading && isNil(listing.error) && isNil(listing.nextCursor)
}

// Everything starts unticked: one block per top-level item, so the reviewer ticks what syncs.
export const uncheckedTopLevelOf = (root, items, nestedRoots = []) =>
  items
    .map((item) => ({
      drive_id: root.driveId,
      item_id: item.item_id,
      ancestorIds: isNil(root.rootId) ? [] : [root.rootId],
      blocked: true,
    }))
    .filter((change) => !isNestedRoot(nestedRoots, change))

// Ready once every root is listed and something is ticked, unless there is nothing to tick.
export const canStartSync = ({ roots, cache, changes, nestedRoots = [] }) => {
  if (!roots.every((root) => isListingComplete(cache, root))) {
    return false
  }
  const choices = roots.flatMap((root) => topLevelItemsOf(cache, root, nestedRoots))

  return isEmpty(choices) || choices.some((item) => !effectiveBlockState(item, changes).blocked)
}

// Only blocks go out: the server holds none yet, so an unblock changes nothing.
export const reviewPayload = (changes) =>
  changes.filter((change) => change.blocked).map(({ drive_id, item_id }) => ({ drive_id, item_id }))

const withReviewStatus = (row) => ({
  ...row,
  status: row.status === PENDING_STATUS ? NOT_SYNCED_YET_STATUS : row.status,
  childRows: row.childRows.map(withReviewStatus),
})

// Today's Manage files tree under the root, checkboxes and all.
export const reviewRowsOf = ({ root, cache, changes }) => {
  const [rootRow] = browseRowsOf({ roots: [root], drives: [], cache, changes })

  return rootRow.childRows.map(withReviewStatus)
}
